import React, { useCallback, useEffect, useRef, useState } from "react";
import AgentRegistry from "./components/AgentRegistry";
import {
  fetchHashrateData,
  fetchMarketData,
  fetchGitHubPRs,
  fetchOotleSummary,
} from "../api/external-data-sources";

const external = { target: "_blank", rel: "noopener noreferrer" };
const donationAddress =
  "1213nhRFy9S3tevPWJyThkZpEuDruaa4L4LNLEgzAuc4PG6dL862KzsijZgyg68qrcKS2U3hCqzjkiJHdasrTDj3pwf";
function Icon({ name = "arrow", size = 20 }) {
  const paths = {
    grid: "M3 3h7v7H3z M14 3h7v7h-7z M3 14h7v7H3z M14 14h7v7h-7z",
    bolt: "M13 2 4 14h7l-1 8 10-13h-7z",
    layers: "m12 3 10 5-10 5L2 8z M2 12l10 5 10-5 M2 16l10 5 10-5",
    code: "m8 6-6 6 6 6 M16 6l6 6-6 6 M14 3l-4 18",
    arrow: "M7 17 17 7 M7 7h10v10",
    refresh: "M20 7a9 9 0 1 0 1 9 M20 2v6h-6",
    globe:
      "M21 12a9 9 0 1 1-18 0 9 9 0 0 1 18 0 M3 12h18 M12 3c5 5 5 13 0 18 M12 3c-5 5-5 13 0 18",
    branch: "M6 3v12a6 6 0 0 0 6 6 M6 9h6a6 6 0 0 0 6-6",
    chip: "M6 6h12v12H6z M9 1v5 M15 1v5 M9 18v5 M15 18v5 M1 9h5 M1 15h5 M18 9h5 M18 15h5",
  };
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.5"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <path d={paths[name]} />
    </svg>
  );
}
function Link({ href, children, className = "text-link" }) {
  return (
    <a href={href} className={className} {...external}>
      {children}
      <Icon />
    </a>
  );
}
const metrics = [
  ["network_sha", "SHA3x", "TH/s", "Network hashrate", "bolt", "lime"],
  [
    "network_randx",
    "RandomX · Merge",
    "GH/s",
    "Monero merge mining",
    "layers",
    "purple",
  ],
  [
    "network_randxt",
    "RandomX · Tari",
    "MH/s",
    "Tari-only mining",
    "chip",
    "blue",
  ],
  [
    "network_cuckaroo",
    "Cuckaroo29",
    "kGPS",
    "Network hashrate",
    "grid",
    "orange",
  ],
];
const nodes = [
  "node.taritracker.com:18145",
  "default-0.tari.jagtech.io:18145",
  "default-1.tari.jagtech.io:18145",
  "default-2.tari.jagtech.io:18145",
  "eu-default-0.tari.jagtech.io:18145",
  "eu-default-1.tari.jagtech.io:18145",
  "eu-default-2.tari.jagtech.io:18145",
];
const format = (n) =>
  n != null && Number.isFinite(Number(n))
    ? Number(n).toLocaleString("en-US", { maximumFractionDigits: 2 })
    : "—";
const protocols = [
  [
    "01 / THE FOUNDATION",
    "layers",
    "lime",
    "Built on a strong base.",
    "Explore Tari’s proof-of-work mainchain, network activity, and mining ecosystem.",
    "Explore the mainchain",
    "https://textexplore.tari.com",
  ],
  [
    "02 / THE NEXT LAYER",
    "code",
    "purple",
    "Ideas become assets.",
    "Discover Ootle, Tari’s ecosystem for digital assets and smart contracts.",
    "Discover Ootle",
    "https://ootle.tari.com",
  ],
  [
    "03 / YOUR WAY IN",
    "bolt",
    "blue",
    "A universe to explore.",
    "Meet Tari Universe and start exploring mining from your desktop.",
    "Meet Tari Universe",
    "https://universe.tari.com",
  ],
];

export default function MarketingPage() {
  const [network, setNetwork] = useState(null);
  const [market, setMarket] = useState(null);
  const [busy, setBusy] = useState(true);
  const [errors, setErrors] = useState([]);
  const [updated, setUpdated] = useState(null);
  const [prs, setPrs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("All projects");
  const [summary, setSummary] = useState([]);
  const [expanded, setExpanded] = useState(false);
  const [copied, setCopied] = useState(false);
  const [donationCopy, setDonationCopy] = useState("");
  const mounted = useRef(false);
  const inFlight = useRef(false);
  const refresh = useCallback(async () => {
    if (inFlight.current) return;
    inFlight.current = true;
    setBusy(true);
    const results = await Promise.allSettled([
      fetchHashrateData(),
      fetchMarketData(),
    ]);
    if (mounted.current) {
      const failures = [];
      if (results[0].status === "fulfilled") {
        setNetwork(results[0].value);
        setUpdated(new Date());
      } else failures.push("Network statistics");
      if (results[1].status === "fulfilled") setMarket(results[1].value);
      else failures.push("Market data");
      setErrors(failures);
      setBusy(false);
    }
    inFlight.current = false;
  }, []);
  useEffect(() => {
    mounted.current = true;
    refresh();
    const timer = setInterval(refresh, 60000);
    fetchGitHubPRs()
      .then((data) => {
        if (mounted.current) setPrs(data);
      })
      .catch(() => {})
      .finally(() => {
        if (mounted.current) setLoading(false);
      });
    fetchOotleSummary()
      .then((text) => {
        if (mounted.current && typeof text === "string")
          setSummary(
            text
              .split(/^#{1,2} /m)
              .slice(1)
              .map((s) => ({
                title: s.split("\n")[0],
                body: s
                  .split("\n")
                  .slice(1)
                  .join(" ")
                  .replace(/\[([^\]]+)\]\([^)]+\)/g, "$1")
                  .replace(/[*#>]/g, "")
                  .trim(),
              }))
              .filter((s) => s.body)
              .slice(0, 3),
          );
      })
      .catch(() => {});
    return () => {
      mounted.current = false;
      clearInterval(timer);
    };
  }, [refresh]);
  async function copy() {
    try {
      await navigator.clipboard.writeText("rpc.tari.com");
      setCopied(true);
    } catch {
      setCopied(false);
    }
  }
  async function copyDonation() {
    try {
      await navigator.clipboard.writeText(donationAddress);
      setDonationCopy("Wallet address copied.");
    } catch {
      setDonationCopy("Select and copy the wallet address below.");
    }
  }
  const filtered = prs.filter(
    (pr) => filter === "All projects" || pr.repoName === filter,
  );
  const status = busy
    ? "Fetching latest data"
    : errors.includes("Network statistics")
      ? network
        ? "Last known data · refresh unavailable"
        : "Network data unavailable"
      : updated
        ? `Updated ${updated.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}`
        : "Waiting for data";
  return (
    <div className="tracker">
      <a className="skip-link" href="#main">
        Skip to dashboard
      </a>
      <aside className="sidebar">
        <a href="#" className="brand">
          <img src="/XTM.svg" alt="" />
          <span>
            tari<span className="brand-light">tracker</span>
            <small>THE NETWORK, AT A GLANCE</small>
          </span>
        </a>
        <div className="nav-label">WORKSPACE</div>
        <nav aria-label="Main navigation">
          {[
            ["overview", "grid", "Overview"],
            ["activity", "branch", "Developer activity"],
            ["resources", "globe", "Resources"],
            ["protocol", "layers", "Tari protocol"],
            ["testnet", "code", "Ootle testnet"],
            ["ai-tools", "chip", "AI Resources"],
            ["donate", "bolt", "Support the tracker"],
          ].map(([id, icon, label]) => (
            <a key={id} href={`#${id}`}>
              <Icon name={icon} />
              {label}
            </a>
          ))}
        </nav>
        <div className="sidebar-bottom">
          <Icon name="globe" />
          <h3>Better together.</h3>
          <p>
            Connect with the people
            <br />
            building and mining Tari.
          </p>
          <Link href="https://community.tari.com" className="community-link">
            Join the forum
          </Link>
          <div className="sidebar-credit">Independent. Open source. ↗</div>
        </div>
      </aside>
      <div className="workspace">
        <header className="topbar">
          <div className="breadcrumb">
            Workspace <span>/</span> <strong>Overview</strong>
          </div>
          <div className="topbar-actions">
            <span className="network-pill">Tari mainnet</span>
            <Link
              href="https://github.com/kstrycharz/tariblocktools"
              className="button primary"
            >
              Download tools
            </Link>
          </div>
        </header>
        <main id="main">
          <section id="overview">
            <div className="page-heading">
              <div>
                <div className="eyebrow">YOUR WINDOW INTO TARI</div>
                <h1>
                  A little clarity. A lot of possibility<span>.</span>
                </h1>
                <p>
                  Network statistics, ecosystem updates, and everything you need
                  to explore Tari.
                </p>
              </div>
              <button
                className="button secondary"
                onClick={refresh}
                disabled={busy}
              >
                <Icon name="refresh" />
                {busy ? "Refreshing…" : "Refresh data"}
              </button>
            </div>
            <div className="section-heading">
              <h2>
                Network overview <span className="tag">MAINNET</span>
              </h2>
              <span className="update-label" role="status">
                <i
                  className={
                    network && !errors.includes("Network statistics")
                      ? "status-dot"
                      : "status-dot muted"
                  }
                />
                {status}
              </span>
            </div>
            {errors.length > 0 && (
              <div className="notice" role="status">
                {errors.join(" and ")} could not be refreshed. Available values
                are retained.{" "}
                <button onClick={refresh} disabled={busy}>
                  Try again ↗
                </button>
              </div>
            )}
            <div className="metrics">
              {metrics.map(([key, label, unit, detail, icon, color]) => (
                <article className="metric-card" key={key}>
                  <div className="metric-top">
                    <span className={`icon-box ${color}`}>
                      <Icon name={icon} />
                    </span>
                    <span>{label}</span>
                  </div>
                  <div className="metric-value">
                    {format(network?.[key])}
                    <span>{unit}</span>
                  </div>
                  <div className="metric-footer">
                    <span>{detail}</span>
                    <span className={`small-mark ${color}`}>⌁</span>
                  </div>
                </article>
              ))}
            </div>
            <div className="network-strip">
              <div>
                <span className="strip-icon">
                  <Icon name="layers" />
                </span>
                <span>
                  Chain height
                  <strong>
                    {format(network?.tip)} <small>blocks</small>
                  </strong>
                </span>
              </div>
              <div>
                <span className="strip-icon">
                  <img src="/XTM.svg" alt="" />
                </span>
                <span>
                  XTM price
                  <strong>
                    {market?.price != null &&
                    Number.isFinite(Number(market.price))
                      ? `$${Number(market.price).toLocaleString("en-US", { maximumFractionDigits: 6 })}`
                      : "—"}{" "}
                    <small>USD</small>
                  </strong>
                </span>
              </div>
              <div className="strip-note">
                <Icon name="refresh" />
                <span>
                  Always in the loop
                  <small>Statistics refresh every 60 seconds</small>
                </span>
              </div>
              <Link href="https://textexplore.tari.com">Open explorer</Link>
            </div>
          </section>
          <div className="content-grid">
            <section className="panel" id="activity">
              <div className="panel-heading">
                <div>
                  <div className="eyebrow">BUILDING IN THE OPEN</div>
                  <h2>Developer activity</h2>
                </div>
                <Link href="https://github.com/tari-project">GitHub</Link>
              </div>
              <div className="tabs" aria-label="Filter developer activity">
                {["All projects", "tari", "tari-dan", "tari-universe"].map(
                  (tab) => (
                    <button
                      key={tab}
                      aria-pressed={filter === tab}
                      className={filter === tab ? "selected" : ""}
                      onClick={() => setFilter(tab)}
                    >
                      {tab}
                    </button>
                  ),
                )}
              </div>
              <div className="pr-list" aria-live="polite">
                {loading ? (
                  <div className="empty-state">
                    Fetching the latest pull requests…
                  </div>
                ) : filtered.length ? (
                  filtered.slice(0, 5).map((pr) => (
                    <a
                      className="pr-row"
                      key={pr.id}
                      href={pr.html_url}
                      {...external}
                    >
                      <span className="pr-icon">
                        <Icon name="branch" />
                      </span>
                      <div>
                        <div className="pr-title">{pr.title}</div>
                        <div className="pr-meta">
                          <span>{pr.repoName}</span> #{pr.number} ·{" "}
                          {pr.user?.login} ·{" "}
                          {new Date(pr.updated_at).toLocaleDateString("en-US", {
                            month: "short",
                            day: "numeric",
                          })}
                        </div>
                      </div>
                      <span className="pr-state">
                        {pr.draft ? "Draft" : "Open"}
                      </span>
                      <Icon />
                    </a>
                  ))
                ) : (
                  <div className="empty-state">
                    No activity is available for this selection.
                    <br />
                    <a href="https://github.com/tari-project" {...external}>
                      Explore the projects on GitHub ↗
                    </a>
                  </div>
                )}
              </div>
              <div className="panel-foot">
                The latest open pull requests across the Tari ecosystem.
              </div>
            </section>
            <section className="panel" id="resources">
              <div className="panel-heading">
                <div>
                  <div className="eyebrow">GOOD PLACES TO START</div>
                  <h2>Your Tari toolkit</h2>
                </div>
                <Icon name="globe" />
              </div>
              <div className="resource-links">
                {[
                  [
                    "globe",
                    "Community forum",
                    "Discussions, ideas, and updates",
                    "https://community.tari.com",
                  ],
                  [
                    "globe",
                    "Tracker Telegram",
                    "Connect with the tracker community",
                    "https://t.me/taritracker",
                  ],
                  [
                    "globe",
                    "Tari website",
                    "Meet the ecosystem",
                    "https://tari.com",
                  ],
                  [
                    "code",
                    "Block tools",
                    "Tools for your next discovery",
                    "https://github.com/kstrycharz/tariblocktools",
                  ],
                  [
                    "bolt",
                    "Mining guide",
                    "Find your way into mining",
                    "https://www.youtube.com/watch?v=nboRF30hJG0&t=1s",
                  ],
                ].map(([icon, title, text, href]) => (
                  <Link key={title} href={href} className="resource-row">
                    <span className="resource-icon">
                      <Icon name={icon} />
                    </span>
                    <span>
                      <strong>{title}</strong>
                      <small>{text}</small>
                    </span>
                  </Link>
                ))}
              </div>
              <div className="node-box">
                <div>
                  <span className="nav-label">BASE NODE RPC</span>
                  <span className="tag">ENDPOINT</span>
                </div>
                <div className="node-address">
                  <code>rpc.tari.com</code>
                  <button onClick={copy} aria-label="Copy RPC endpoint">
                    {copied ? "Copied ✓" : "Copy"}
                  </button>
                </div>
              </div>
              <details className="pool-nodes">
                <summary>
                  P2Pool endpoints <span>{nodes.length} nodes</span>
                </summary>
                {nodes.map((node) => (
                  <a href={`http://${node}`} key={node} {...external}>
                    {node} ↗
                  </a>
                ))}
              </details>
            </section>
          </div>
          <section id="protocol" className="protocol">
            <div className="section-heading">
              <div>
                <div className="eyebrow">
                  ONE ECOSYSTEM. MANY POSSIBILITIES.
                </div>
                <h2>Get to know the protocol</h2>
              </div>
              <Link href="https://tari.com">Explore Tari</Link>
            </div>
            <div className="protocol-grid">
              {protocols.map(
                ([number, icon, color, title, text, label, href]) => (
                  <article className="protocol-card" key={number}>
                    <span className="protocol-number">{number}</span>
                    <div className={`protocol-art ${color}`}>
                      <Icon name={icon} size={52} />
                    </div>
                    <h3>{title}</h3>
                    <p>{text}</p>
                    <Link href={href}>{label}</Link>
                  </article>
                ),
              )}
            </div>
          </section>
          <section className="ootle-panel">
            <div>
              <span className="icon-box purple">
                <Icon name="code" />
              </span>
              <div>
                <h3>From the Ootle ecosystem</h3>
                <p>Documentation and ideas for your next build.</p>
              </div>
            </div>
            <button
              className="button secondary"
              aria-expanded={expanded}
              onClick={() => setExpanded(!expanded)}
            >
              {expanded ? "Close overview" : "Explore overview"}{" "}
              <span>{expanded ? "−" : "+"}</span>
            </button>
            {expanded && (
              <div className="ootle-content">
                {summary.length ? (
                  summary.map((section) => (
                    <article key={section.title}>
                      <h4>{section.title}</h4>
                      <p>
                        {section.body.slice(0, 350)}
                        {section.body.length > 350 ? "…" : ""}
                      </p>
                    </article>
                  ))
                ) : (
                  <p>
                    Explore the Ootle documentation for digital assets, smart
                    contracts, and developer resources.
                  </p>
                )}
                <Link href="https://ootle.tari.com">
                  Open Ootle documentation
                </Link>
              </div>
            )}
          </section>
          <section
            id="testnet"
            className="testnet-section"
            aria-labelledby="testnet-title"
          >
            <div className="section-heading">
              <div>
                <div className="eyebrow">BUILT BY THE TRACKER COMMUNITY</div>
                <h2 id="testnet-title">
                  Explore Ootle testnet{" "}
                  <span className="tag">LAYER 2 · TESTNET</span>
                </h2>
              </div>
            </div>
            <p className="testnet-intro">
              Try a community-built app on Tari’s Layer 2, or pick up testnet
              tokens for your next experiment.
            </p>
            <div className="testnet-grid">
              <article className="testnet-card">
                <span className="icon-box purple">
                  <Icon name="code" />
                </span>
                <div className="testnet-card-body">
                  <div className="eyebrow">A MESSAGE BOARD ON L2</div>
                  <h3>Hauntboard</h3>
                  <p>
                    A community-built message board on Ootle. Explore
                    conversations and try an app running on the Layer 2 testnet.
                  </p>
                  <Link
                    href="https://hauntboard.taritracker.com"
                    className="button secondary"
                  >
                    Open Hauntboard
                  </Link>
                </div>
              </article>
              <article className="testnet-card">
                <span className="icon-box lime">
                  <Icon name="bolt" />
                </span>
                <div className="testnet-card-body">
                  <div className="eyebrow">TOKENS FOR YOUR NEXT EXPERIMENT</div>
                  <h3>Ootle testnet faucet</h3>
                  <p>
                    Get Layer 2 testnet tokens to explore apps and experiment
                    with Ootle. These tokens are for testing and have no
                    monetary value.
                  </p>
                  <Link
                    href="https://testnet-faucet.taritracker.com/"
                    className="button primary"
                  >
                    Get testnet tokens
                  </Link>
                </div>
              </article>
            </div>
          </section>
          <AgentRegistry />
          <section
            id="donate"
            className="donation-panel"
            aria-labelledby="donation-title"
          >
            <div className="donation-heading">
              <span className="icon-box lime">
                <Icon name="bolt" />
              </span>
              <div>
                <div className="eyebrow">A LITTLE SUPPORT GOES A LONG WAY</div>
                <h2 id="donation-title">Support Tari Tracker</h2>
              </div>
              <span className="tag">L1 MAINNET · XTM</span>
            </div>
            <p>
              If the tracker, tools, or testnet apps have helped you, consider a
              donation to support their continued development.
            </p>
            <div className="donation-wallet">
              <label htmlFor="donation-address">
                Tari Layer 1 donation address
              </label>
              <div className="donation-address-row">
                <textarea
                  id="donation-address"
                  readOnly
                  value={donationAddress}
                  rows={3}
                  spellCheck={false}
                />
                <button className="button primary" onClick={copyDonation}>
                  Copy wallet address
                </button>
              </div>
              <div className="donation-copy-status" role="status">
                {donationCopy || "Send XTM on Tari L1 mainnet."}
              </div>
            </div>
          </section>
          <footer>
            <a href="#" className="footer-brand">
              tari<span>tracker</span>
            </a>
            <span>An independent window into the Tari ecosystem.</span>
            <Link href="https://github.com/kstrycharz/azurestatictesting">
              Open source
            </Link>
          </footer>
        </main>
      </div>
    </div>
  );
}
